import { useCallback, useEffect, useRef, useState } from "react";
import { QrCode } from "@phosphor-icons/react";
import { Modal } from "@/components/lims";
import { sampleIdFromScan } from "@/lib/scan";

/**
 * Look a specimen up by pointing a camera at its code.
 *
 * Decoding uses the browser's own `BarcodeDetector`, not a bundled decoder. A
 * QR library is a few hundred kilobytes of WASM or minified JavaScript for one
 * button, and this codebase already declines that trade in the other direction
 * — `reporting.qr_svg` writes QR modules and PDF bytes by hand rather than take
 * a dependency. Where the API is missing the panel says so and points at the
 * two routes that always work, because a scanner that silently fails is worse
 * than one that admits it cannot help.
 *
 * The always-works routes matter more than this button: a bench scanner in
 * keyboard-wedge mode types the decoded URL into the search box, and the search
 * understands it (`sampleIdFromScan`). This is for a laptop with a webcam and
 * a tube in somebody's hand.
 */
const DETECTION_INTERVAL_MS = 250;

function detectorAvailable() {
  return typeof window !== "undefined" && "BarcodeDetector" in window;
}

export default function ScanLookup({ onFound }) {
  const [open, setOpen] = useState(false);
  const [status, setStatus] = useState(null);
  const videoRef = useRef(null);
  const streamRef = useRef(null);

  const stop = useCallback(() => {
    streamRef.current?.getTracks().forEach((track) => track.stop());
    streamRef.current = null;
  }, []);

  const close = useCallback(() => {
    stop();
    setOpen(false);
    setStatus(null);
  }, [stop]);

  useEffect(() => {
    if (!open) return undefined;

    if (!detectorAvailable()) {
      setStatus("unsupported");
      return undefined;
    }

    let cancelled = false;
    let timer = null;
    const detector = new window.BarcodeDetector({ formats: ["qr_code"] });

    const start = async () => {
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          // The rear camera on a tablet at the bench; ignored on a laptop.
          video: { facingMode: "environment" },
        });
        if (cancelled) {
          stream.getTracks().forEach((track) => track.stop());
          return;
        }
        streamRef.current = stream;
        if (videoRef.current) {
          videoRef.current.srcObject = stream;
          await videoRef.current.play().catch(() => {});
        }
        setStatus("scanning");
        timer = window.setInterval(async () => {
          if (!videoRef.current) return;
          try {
            const codes = await detector.detect(videoRef.current);
            for (const code of codes) {
              const id = sampleIdFromScan(code.rawValue);
              if (id) {
                window.clearInterval(timer);
                onFound(id);
                close();
                return;
              }
            }
            // A code that reads but is not one of ours — a shipping label, a
            // reagent barcode, a colleague's Wi-Fi QR. Say which, rather than
            // looking broken while the camera keeps running.
            if (codes.length) setStatus("unrecognized");
          } catch {
            // A single failed frame is ordinary; the next one usually reads.
          }
        }, DETECTION_INTERVAL_MS);
      } catch {
        if (!cancelled) setStatus("denied");
      }
    };

    start();

    return () => {
      cancelled = true;
      if (timer) window.clearInterval(timer);
      stop();
    };
  }, [open, onFound, close, stop]);

  return (
    <>
      <button
        type="button"
        onClick={() => setOpen(true)}
        data-testid="scan-lookup-open"
        title="Find a sample by scanning its code"
        className="inline-flex shrink-0 items-center gap-1.5 rounded-md border border-slate-200 bg-white px-3 py-2 text-sm text-slate-600 transition-colors hover:bg-slate-50"
      >
        <QrCode size={16} /> Scan
      </button>

      <Modal open={open} onClose={close} title="Scan a specimen code" testid="scan-lookup-modal">
        {status === "unsupported" ? (
          <div className="space-y-3 text-sm text-meta" data-testid="scan-unsupported">
            <p className="text-ink">This browser cannot decode a code from the camera.</p>
            <p>Two things still work without it:</p>
            <ul className="list-disc space-y-1 pl-5">
              <li>
                A handheld scanner in keyboard mode — put the cursor in the search
                box and scan. The search understands the scanned link.
              </li>
              <li>Paste a scanned link into the search box.</li>
            </ul>
            <p className="text-xs">Camera decoding is available in Chrome and Edge.</p>
          </div>
        ) : (
          <div className="space-y-3">
            <div className="overflow-hidden rounded-lg border border-line bg-ink">
              <video
                ref={videoRef}
                data-testid="scan-video"
                className="h-64 w-full object-cover"
                muted
                playsInline
              />
            </div>
            <p className="text-center text-sm text-meta" data-testid="scan-status" role="status">
              {status === "denied"
                ? "No camera available, or permission was declined."
                : status === "unrecognized"
                  ? "That code is not a specimen code from this system."
                  : "Point the camera at the code on a label, tube, or screen."}
            </p>
          </div>
        )}
      </Modal>
    </>
  );
}
